import { useMemo } from "react";

import Box from "@/components/Box";
import Loading from "@/components/Loading";
import MultiSelectCircleDropdown from "@/components/MultiSelectCircleDropdown";
import { SpanText } from "@/components/Text";
import Group from "@/tpo/Group";
import { USER_TEST_FIELDS_TRUNCATED } from "@/graphql/tpo/results/types";
import { gql, useQuery } from "@apollo/client";

const USER_TESTS_QUERY = gql`
  query UserTestsQuery($userId: ID!, $includeUnapproved: Boolean!, $allTests: Boolean!, $organisationId: ID!) {
    userTests(userId: $userId, includeUnapproved: $includeUnapproved, allTests: $allTests, organisationId: $organisationId) {
      ...UserTestFieldsTruncated
      orderTestItem {
        id
        order {
          id
        }
      }
    }
  }
  ${USER_TEST_FIELDS_TRUNCATED}
`;

export default function UserTestMultiSelect({
  value,
  onChange,
  userId,
  placeholder = "Select test results...",
  disabled,
  excludeTestIds = [],
  maxSelections,
  singleSelect = false,
  organisationId,
  sx
}) {
  const { data, loading } = useQuery(USER_TESTS_QUERY, {
    variables: {
      userId: userId,
      includeUnapproved: true,
      allTests: true,
      organisationId: organisationId
    },
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first",
    skip: !userId
  });

  const userTests = data?.userTests || [];

  // Filter out excluded test IDs
  const filteredUserTests = userTests.filter(userTest => !excludeTestIds.includes(userTest.id));

  const options = useMemo(
    () =>
      filteredUserTests.map(userTest => {
        const orderId = userTest?.orderTestItem?.order?.id;
        const createdDate = userTest.createdDateFormatted;
        const testName = userTest.name || userTest.testProduct?.name || "Test";
        
        return {
          value: userTest.id,
          label: testName,
          content: (
            <Box
              display="flex"
              justifyContent="space-between"
              textAlign="left"
              fontSize={14}
              flexWrap="wrap"
            >
              <Group flex={1}>
                <Box>
                  <Box>
                    <SpanText lineHeight={1} color="white">
                      {testName}
                    </SpanText>
                  </Box>
                  {orderId && (
                    <Group mt={1}>
                      <SpanText lineHeight={1} color="#818181">
                        {orderId}
                      </SpanText>
                      {createdDate && (
                        <>
                          <SpanText lineHeight={1} color="#818181" mx={1}>
                            |
                          </SpanText>
                          <SpanText lineHeight={1} color="#818181">
                            {createdDate}
                          </SpanText>
                        </>
                      )}
                    </Group>
                  )}
                </Box>
              </Group>
            </Box>
          )
        };
      }),
    [filteredUserTests]
  );

  // Convert single value to array for multi-select component
  const arrayValue = useMemo(() => {
    if (singleSelect) {
      return value ? [value] : [];
    }
    return Array.isArray(value) ? value : value ? [value] : [];
  }, [value, singleSelect]);

  const handleChange = selectedValues => {
    if (disabled) return;

    // Apply max selections limit
    const limitedValues =
      maxSelections && selectedValues.length > maxSelections
        ? selectedValues.slice(0, maxSelections)
        : selectedValues;

    if (singleSelect) {
      // For single select, return the first value or null
      onChange(limitedValues.length > 0 ? limitedValues[0] : null);
    } else {
      onChange(limitedValues);
    }
  };

  if (!userId) {
    return (
      <Box
        py={15}
        px={20}
        borderRadius={5}
        border="1px solid"
        borderColor="haze"
        fontFamily="gilroyMedium"
        fontSize={14}
        color="gray"
        {...sx}
      >
        Please select a patient first
      </Box>
    );
  }

  if (loading && options.length === 0) {
    return (
      <Box
        py={15}
        px={20}
        borderRadius={5}
        border="1px solid"
        borderColor="haze"
        display="flex"
        justifyContent="center"
        {...sx}
      >
        <Loading />
      </Box>
    );
  }

  if (options.length === 0) {
    return (
      <Box
        py={15}
        px={20}
        borderRadius={5}
        border="1px solid"
        borderColor="haze"
        fontFamily="gilroyMedium"
        fontSize={14}
        color="gray"
        {...sx}
      >
        No test results found
      </Box>
    );
  }

  return (
    <MultiSelectCircleDropdown
      label={
        arrayValue.length > 0
          ? options
              .filter(opt => arrayValue.includes(opt.value))
              .map(opt => opt.label)
              .join(", ")
          : placeholder
      }
      options={options}
      value={arrayValue}
      onChange={handleChange}
      placeholder={placeholder}
      disabled={disabled}
      singleSelect={singleSelect}
      buttonProps={{
        sx: {
          color: "white",
          borderRadius: 5,
          ...sx
        }
      }}
      dropdownProps={{
        bg: "dark"
      }}
    />
  );
}
